/** Una página: portada, icono, título y el editor, con autoguardado.
 *
 * El guardado va con espera (`debounce`) y **se vacía al salir**: si se cambia de página o se
 * cierra la pestaña con algo sin guardar, se manda antes de irse. Sin eso, escribir y navegar
 * rápido perdería el último trozo.
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { Link, useParams } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import Limite from '../componentes/Limite'
import { useTemaOscuro } from '../componentes/tema'
import { Cargando, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { hace } from '../textos'
import { IconoDePagina } from './Arbol'
import Acceso from './Acceso'
import BaseVista from './bd/BaseVista'
import { conOpcionNueva } from './bd/logica'
import PropiedadesFila from './bd/PropiedadesFila'
import type { Esquema } from './bd/tipos'
import Comentarios from './Comentarios'
import { useEspacio } from './contexto'
import Historial from './Historial'
import type { ManejoDelEditor } from './Editor'
import { fondoDePortada, SelectorDeEmoji, SelectorDePortada } from './Iconos'
import { Menu, type Opcion } from './Menu'
import { PLANTILLAS } from './plantillas'
import type { Bloque, Pagina } from './tipos'

// El editor es lo más pesado del portal; sólo se descarga al abrir una página.
const Editor = lazy(() => import('./Editor'))

type Estado = 'guardado' | 'guardando' | 'pendiente' | 'error'

function vacio(bloques: Bloque[] | null): boolean {
  if (!bloques || bloques.length === 0) return true
  if (bloques.length > 1) return false
  const b = bloques[0] as { type?: string; content?: unknown[] | string }
  if (b.type !== 'paragraph') return false
  const c = b.content
  return !c || (Array.isArray(c) && c.length === 0) || c === ''
}

export default function PaginaVista() {
  const { id } = useParams()
  const { servidor, avisar, ruta } = usePortal()
  const { rutaPaginas, tocarNodo, alternarFavorita, aLaPapelera, duplicar, crear, mover } =
    useEspacio()

  const [pagina, setPagina] = useState<Pagina | null>(null)
  const [error, setError] = useState('')
  const [estado, setEstado] = useState<Estado>('guardado')
  const [selector, setSelector] = useState<'emoji' | 'portada' | null>(null)
  const [menu, setMenu] = useState(false)
  const [dialogo, setDialogo] = useState<'acceso' | 'historial' | null>(null)
  // Sube al restaurar una versión: fuerza a que el editor se vuelva a montar con el contenido nuevo.
  const [recarga, setRecarga] = useState(0)
  const [contenidoVacio, setContenidoVacio] = useState(false)
  // Si la página es una fila, la base a la que pertenece: de ahí salen sus propiedades.
  const [baseDeFila, setBaseDeFila] = useState<Pagina | null>(null)
  // Alguien guardó la página antes que nosotros: no se sigue guardando hasta que se decida qué hacer.
  const [conflicto, setConflicto] = useState(false)

  const editor = useRef<ManejoDelEditor>(null)
  // La cola de guardados de esquema y de propiedades de esta fila: van de uno en uno y en orden.
  const esquemaGuardando = useRef<Promise<void>>(Promise.resolve())
  const botonIcono = useRef<HTMLButtonElement>(null)
  const botonPortada = useRef<HTMLButtonElement>(null)
  const botonMenu = useRef<HTMLButtonElement>(null)

  // Lo pendiente de guardar se acumula aquí y se manda de una vez.
  const pendiente = useRef<Record<string, unknown>>({})
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paginaId = useRef<number | null>(null)
  // La versión del texto que conocemos, que se manda al guardar para detectar conflictos, y la
  // cola que hace que los guardados salgan de uno en uno (si salieran a la vez, el segundo llevaría
  // una versión que el primero ya ha cambiado y se tomaría por un conflicto).
  const versionConocida = useRef(0)
  const cola = useRef<Promise<void>>(Promise.resolve())
  const enConflicto = useRef(false)

  const vaciar = useCallback((): Promise<void> => {
    if (temporizador.current) clearTimeout(temporizador.current)
    temporizador.current = null
    const enviar = async () => {
      if (enConflicto.current) return
      const cambios = pendiente.current
      const donde = paginaId.current
      if (donde == null || Object.keys(cambios).length === 0) return
      pendiente.current = {}
      setEstado('guardando')
      const tocaElTexto = 'content' in cambios || 'title' in cambios
      try {
        const nueva = await api.patch<Pagina>(`${rutaPaginas}/${donde}`, {
          ...cambios,
          ...(tocaElTexto ? { base_version: versionConocida.current } : {}),
        })
        if (paginaId.current === donde) {
          versionConocida.current = nueva.version
          setPagina((p) => (p ? { ...p, version: nueva.version, updated_at: nueva.updated_at,
                                editada_por: nueva.editada_por } : p))
          setEstado(Object.keys(pendiente.current).length ? 'pendiente' : 'guardado')
        }
      } catch (e) {
        // Se devuelve a la cola para reintentar con el siguiente cambio.
        pendiente.current = { ...cambios, ...pendiente.current }
        if (e instanceof ErrorDeApi && e.codigo === 409) {
          enConflicto.current = true
          setConflicto(true)
          setEstado('error')
          return
        }
        setEstado('error')
        if (e instanceof ErrorDeApi && e.codigo === 404) setError('Esta página ya no existe.')
      }
    }
    cola.current = cola.current.then(enviar, enviar)
    return cola.current
  }, [rutaPaginas])

  /** «Cargar la suya»: se tira lo nuestro sin guardar y se abre la versión que hay en el servidor. */
  const resolverConflicto = async (quedarseConLoMio: boolean) => {
    const donde = paginaId.current
    if (donde == null) return
    try {
      const fresca = await api.get<Pagina>(`${rutaPaginas}/${donde}`)
      versionConocida.current = fresca.version
      enConflicto.current = false
      setConflicto(false)
      if (quedarseConLoMio) {
        // La versión nueva ya es la base: lo nuestro se guarda encima, a sabiendas.
        setPagina((p) => (p ? { ...p, version: fresca.version } : p))
        setEstado('pendiente')
        void vaciar()
      } else {
        pendiente.current = {}
        setPagina(fresca)
        setContenidoVacio(vacio(fresca.content))
        tocarNodo(fresca.id, { title: fresca.title })
        setRecarga((n) => n + 1)
        setEstado('guardado')
      }
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido abrir la versión nueva.', 'malo')
    }
  }

  const guardar = useCallback(
    (cambios: Record<string, unknown>, espera = 900) => {
      pendiente.current = { ...pendiente.current, ...cambios }
      if (enConflicto.current) return        // se guarda cuando se decida qué hacer
      setEstado('pendiente')
      if (temporizador.current) clearTimeout(temporizador.current)
      temporizador.current = setTimeout(() => void vaciar(), espera)
    },
    [vaciar],
  )

  // Cargar la página al cambiar de id, guardando antes lo que quedara de la anterior.
  useEffect(() => {
    let vivo = true
    setPagina(null)
    setBaseDeFila(null)
    setError('')
    setEstado('guardado')
    setSelector(null)
    setConflicto(false)
    enConflicto.current = false
    paginaId.current = null
    api
      .get<Pagina>(`${rutaPaginas}/${id}`)
      .then((p) => {
        if (!vivo) return
        paginaId.current = p.id
        versionConocida.current = p.version
        setPagina(p)
        setContenidoVacio(vacio(p.content))
        if (p.kind === 'row' && p.parent_id) {
          api.get<Pagina>(`${rutaPaginas}/${p.parent_id}`)
            .then((b) => vivo && setBaseDeFila(b))
            .catch(() => undefined)
        }
      })
      .catch((e: ErrorDeApi) => vivo && setError(e.message))
    return () => {
      vivo = false
      void vaciar()
    }
  }, [id, rutaPaginas, vaciar])

  // Cerrar la pestaña con cambios sin mandar: se avisa y se intenta guardar.
  useEffect(() => {
    const alSalir = (e: BeforeUnloadEvent) => {
      if (Object.keys(pendiente.current).length === 0) return
      void vaciar()
      e.preventDefault()
    }
    window.addEventListener('beforeunload', alSalir)
    return () => window.removeEventListener('beforeunload', alSalir)
  }, [vaciar])

  const oscuro = useTemaOscuro()

  if (error) {
    return (
      <div className="pagina pagina--estrecha">
        <Vacio titulo="No se puede abrir esta página">
          <p>{error}</p>
          <Link className="boton" to={`/${servidor.id}/paginas`}>Volver</Link>
        </Vacio>
      </div>
    )
  }
  if (!pagina) {
    return (
      <div className="pagina pagina--estrecha">
        <Cargando filas={5} />
      </div>
    )
  }

  const puedeEditar = pagina.puede_editar
  const portada = fondoDePortada(pagina.cover)

  const cambiarTitulo = (titulo: string) => {
    setPagina((p) => (p ? { ...p, title: titulo } : p))
    tocarNodo(pagina.id, { title: titulo })
    guardar({ title: titulo })
  }

  const cambiarIcono = (icono: string | null) => {
    setSelector(null)
    setPagina((p) => (p ? { ...p, icon: icono } : p))
    tocarNodo(pagina.id, { icon: icono })
    guardar({ icon: icono }, 0)
  }

  const cambiarPortada = (cover: string | null) => {
    setSelector(null)
    setPagina((p) => (p ? { ...p, cover } : p))
    guardar({ cover }, 0)
  }

  const alternar = (campo: 'full_width' | 'small_text') => {
    const valor = !pagina[campo]
    setPagina((p) => (p ? { ...p, [campo]: valor } : p))
    guardar({ [campo]: valor }, 0)
  }

  const esquemaDeFila = baseDeFila?.db as unknown as Esquema | undefined

  const subirArchivo = async (archivo: File): Promise<string> => {
    const formulario = new FormData()
    formulario.append('archivo', archivo)
    // Atado a esta página: quien no pueda verla tampoco podrá ver el archivo.
    formulario.append('pagina', String(pagina.id))
    try {
      return (await api.subir<{ url: string }>(`${ruta}/archivos`, formulario)).url
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido subir el archivo.', 'malo')
      throw e
    }
  }

  const exportarMarkdown = async () => {
    const texto = await editor.current?.aMarkdown()
    if (texto == null) return
    const enlace = document.createElement('a')
    enlace.href = URL.createObjectURL(new Blob([texto], { type: 'text/markdown;charset=utf-8' }))
    enlace.download = `${(pagina.title || 'pagina').replace(/[\\/:*?"<>|]/g, '-')}.md`
    enlace.click()
    URL.revokeObjectURL(enlace.href)
  }

  // Las propiedades de una fila se guardan al momento y de una en una: así no se pisan dos
  // cambios seguidos, que es lo que pasaría si se acumularan con el texto de la página.
  const cambiarPropiedad = (propId: string, valor: unknown) => {
    setPagina((p) => {
      if (!p) return p
      const props = { ...(p.props ?? {}) }
      if (valor == null) delete props[propId]
      else props[propId] = valor
      return { ...p, props }
    })
    // Detrás de un posible guardado de esquema (una opción recién creada tiene que existir ya).
    esquemaGuardando.current = esquemaGuardando.current
      .then(() => api.patch(`${rutaPaginas}/${pagina.id}`, { props: { [propId]: valor } }))
      .then(() => undefined)
      .catch((e) =>
        avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido guardar el cambio.', 'malo'),
      )
  }

  const crearOpcionDeFila = (propId: string, nombre: string): string | null => {
    if (!baseDeFila || !esquemaDeFila) return null
    const r = conOpcionNueva(esquemaDeFila, propId, nombre)
    setBaseDeFila({ ...baseDeFila, db: r.esquema as unknown as Record<string, unknown> })
    esquemaGuardando.current = esquemaGuardando.current
      .then(() => api.patch(`${rutaPaginas}/${baseDeFila.id}`, { db: r.esquema }))
      .then(() => undefined)
      .catch((e) =>
        avisar(e instanceof ErrorDeApi ? e.message : 'No se ha podido crear la opción.', 'malo'),
      )
    return r.opcionId
  }

  const aplicarPlantilla = (idPlantilla: string) => {
    const plantilla = PLANTILLAS.find((x) => x.id === idPlantilla)
    if (!plantilla) return
    editor.current?.reemplazar(plantilla.bloques)
    if (!pagina.title) cambiarTitulo(plantilla.titulo)
    if (!pagina.icon) cambiarIcono(plantilla.icono)
    setContenidoVacio(false)
  }

  // Intro en el título baja al editor, como en Notion.
  const teclaTitulo = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      editor.current?.enfocar()
    }
  }

  const opciones: Opcion[] = [
    {
      texto: pagina.favorita ? 'Quitar de favoritos' : 'Añadir a favoritos',
      icono: pagina.favorita ? '★' : '☆',
      alElegir: () => {
        void alternarFavorita(pagina.id).then(() =>
          setPagina((p) => (p ? { ...p, favorita: !p.favorita } : p)),
        )
      },
    },
    {
      texto: pagina.full_width ? 'Ancho normal' : 'Ancho completo',
      icono: '↔',
      alElegir: () => alternar('full_width'),
      separador: false,
    },
    {
      texto: pagina.small_text ? 'Texto normal' : 'Texto pequeño',
      icono: 'Aa',
      alElegir: () => alternar('small_text'),
    },
    { separador: true, texto: '' },
    {
      texto: pagina.publica ? 'Compartir y acceso · publicada' : 'Compartir y acceso',
      icono: pagina.efectivo_ver !== 'member' ? '🔒' : '🔗',
      alElegir: () => setDialogo('acceso'),
    },
    ...(pagina.kind !== 'database'
      ? ([
          { texto: 'Historial de versiones', icono: '🕘', alElegir: () => setDialogo('historial') },
        ] as Opcion[])
      : []),
    ...(pagina.kind === 'page' || pagina.kind === 'row'
      ? ([
          { texto: 'Exportar a Markdown', icono: '⤓', alElegir: () => void exportarMarkdown() },
        ] as Opcion[])
      : []),
    ...(puedeEditar
      ? ([
          { separador: true, texto: '' },
          { texto: 'Duplicar', icono: '⧉', alElegir: () => void duplicar(pagina.id) },
          {
            texto: 'Mover a la raíz',
            icono: '⇱',
            alElegir: () => {
              void mover(pagina.id, null).then(() =>
                setPagina((p) => (p ? { ...p, parent_id: null, camino: [] } : p)),
              )
            },
          },
          {
            texto: 'Mover a la papelera',
            icono: '🗑',
            peligro: true,
            alElegir: () => void aLaPapelera(pagina.id),
          },
        ] as Opcion[])
      : []),
  ]

  const textoEstado: Record<Estado, string> = {
    guardado: puedeEditar ? 'Guardado' : 'Sólo lectura',
    guardando: 'Guardando…',
    pendiente: 'Cambios sin guardar',
    error: 'No se ha podido guardar. Reintentando con el próximo cambio.',
  }

  return (
    <div className={`pagina-marco${pagina.small_text ? ' pagina-marco--pequeno' : ''}`}>
      <div className="pagina-barra">
        <nav className="migas" aria-label="Ruta">
          {pagina.camino.map((m) => (
            <span key={m.id} className="migas__tramo">
              <Link to={`/${servidor.id}/paginas/${m.id}`}>
                <IconoDePagina icono={m.icon} tam={16} /> {m.title || 'Sin título'}
              </Link>
              <span className="migas__sep">/</span>
            </span>
          ))}
          <span className="migas__actual">
            <IconoDePagina icono={pagina.icon} tam={16} /> {pagina.title || 'Sin título'}
          </span>
        </nav>
        <div className="pagina-barra__acciones">
          <span className={`estado estado--${estado}`} role="status" aria-live="polite"
                title={pagina.editada_por ? `Editada por ${pagina.editada_por.nombre} ${hace(pagina.updated_at)}` : undefined}>
            {estado === 'guardado' && pagina.editada_por
              ? `Editada ${hace(pagina.updated_at)}`
              : textoEstado[estado]}
          </span>
          <button
            className="arbol-boton arbol-boton--visible"
            onClick={() => void alternarFavorita(pagina.id).then(() =>
              setPagina((p) => (p ? { ...p, favorita: !p.favorita } : p)))}
            aria-label={pagina.favorita ? 'Quitar de favoritos' : 'Añadir a favoritos'}
            aria-pressed={pagina.favorita}
          >
            {pagina.favorita ? '★' : '☆'}
          </button>
          <button ref={botonMenu} className="arbol-boton arbol-boton--visible"
                  onClick={() => setMenu((v) => !v)} aria-label="Más opciones">
            ⋯
          </button>
          {menu && <Menu ancla={botonMenu} opciones={opciones} alCerrar={() => setMenu(false)}
                         alinear="fin" ancho={230} />}
        </div>
      </div>

      {conflicto && (
        <div className="conflicto" role="alert">
          <div>
            <strong>Alguien más ha guardado esta página mientras la editabas.</strong>
            <span> Tus últimos cambios no se han guardado todavía, para no pisar los suyos.</span>
          </div>
          <div className="fila-botones">
            <button className="boton" onClick={() => void resolverConflicto(false)}>
              Ver su versión (descarta lo mío)
            </button>
            <button className="boton boton--principal" onClick={() => void resolverConflicto(true)}>
              Guardar la mía encima
            </button>
          </div>
        </div>
      )}

      <div className="pagina-scroll">
        {portada && (
          <div className="portada" style={{ background: portada }}>
            {puedeEditar && (
              <div className="portada__acciones">
                <button ref={botonPortada} className="boton"
                        onClick={() => setSelector(selector === 'portada' ? null : 'portada')}>
                  Cambiar portada
                </button>
              </div>
            )}
          </div>
        )}

        <div className={`pagina${pagina.full_width || pagina.kind === 'database' ? ' pagina--completa' : ''}${portada ? ' pagina--con-portada' : ''}`}>
          <div className="pagina__cabecera">
            {pagina.icon && (
              <button
                ref={botonIcono}
                className="pagina__icono"
                onClick={() => puedeEditar && setSelector(selector === 'emoji' ? null : 'emoji')}
                disabled={!puedeEditar}
                aria-label="Cambiar icono"
              >
                {pagina.icon}
              </button>
            )}
            {puedeEditar && (!pagina.icon || !portada) && (
              <div className="pagina__atajos">
                {!pagina.icon && (
                  <button ref={botonIcono} className="boton boton--plano"
                          onClick={() => setSelector('emoji')}>
                    ☺ Añadir icono
                  </button>
                )}
                {!portada && (
                  <button ref={botonPortada} className="boton boton--plano"
                          onClick={() => setSelector('portada')}>
                    🖼 Añadir portada
                  </button>
                )}
              </div>
            )}
          </div>

          <textarea
            className="pagina__titulo"
            value={pagina.title}
            placeholder="Sin título"
            rows={1}
            readOnly={!puedeEditar}
            maxLength={200}
            onChange={(e) => cambiarTitulo(e.target.value.replace(/\n/g, ' '))}
            onKeyDown={teclaTitulo}
            ref={(el) => {
              // Crece con el texto: un título largo se parte en líneas en vez de desplazarse.
              if (el) {
                el.style.height = '0'
                el.style.height = `${el.scrollHeight}px`
              }
            }}
            aria-label="Título de la página"
            autoFocus={puedeEditar && !pagina.title && contenidoVacio}
          />

          {pagina.kind === 'row' && esquemaDeFila && (
            <PropiedadesFila
              esquema={esquemaDeFila}
              props={pagina.props ?? {}}
              editable={puedeEditar}
              alCambiar={cambiarPropiedad}
              alCrearOpcion={crearOpcionDeFila}
            />
          )}

          {puedeEditar && contenidoVacio && pagina.kind === 'page' && (
            <div className="plantillas">
              <div className="plantillas__titulo">Empezar con</div>
              <div className="plantillas__lista">
                {PLANTILLAS.map((t) => (
                  <button key={t.id} className="plantilla" onClick={() => aplicarPlantilla(t.id)}>
                    <span className="plantilla__icono">{t.icono}</span>
                    <span>
                      <strong>{t.nombre}</strong>
                      <small>{t.descripcion}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {pagina.kind === 'database' ? (
            <BaseVista key={pagina.id} base={pagina} />
          ) : (
            <Limite compacto>
              <Suspense fallback={<Cargando filas={4} />}>
                <Editor
                  key={`${pagina.id}.${recarga}`}
                  ref={editor}
                  subir={puedeEditar ? subirArchivo : undefined}
                  inicial={pagina.content}
                  editable={puedeEditar}
                  oscuro={oscuro}
                  alCambiar={(bloques) => {
                    setContenidoVacio(vacio(bloques))
                    guardar({ content: bloques }, 1100)
                  }}
                />
              </Suspense>
            </Limite>
          )}

          {pagina.kind !== 'database' && (
            <Comentarios key={`c${pagina.id}`} paginaId={pagina.id} rutaPaginas={rutaPaginas} />
          )}

          {puedeEditar && pagina.kind === 'page' && (
            <div className="subpagina-nueva">
              <button className="boton boton--plano"
                      onClick={() => void crear({ parent_id: pagina.id })}>
                ＋ Añadir subpágina
              </button>
            </div>
          )}
        </div>
      </div>

      {dialogo === 'acceso' && (
        <Acceso
          pagina={pagina}
          rutaPaginas={rutaPaginas}
          alCambiar={(p) => {
            setPagina((actual) => (actual ? { ...actual, ...p, content: actual.content } : p))
            tocarNodo(p.id, { restringida: p.efectivo_ver !== 'member' })
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo === 'historial' && (
        <Historial
          pagina={pagina}
          rutaPaginas={rutaPaginas}
          alRestaurar={(p) => {
            pendiente.current = {}
            versionConocida.current = p.version
            setPagina(p)
            setContenidoVacio(vacio(p.content))
            tocarNodo(p.id, { title: p.title })
            setRecarga((n) => n + 1)
            setDialogo(null)
            avisar('Versión restaurada.')
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {selector === 'emoji' && (
        <SelectorDeEmoji
          ancla={botonIcono}
          puedeQuitar={!!pagina.icon}
          alElegir={cambiarIcono}
          alCerrar={() => setSelector(null)}
        />
      )}
      {selector === 'portada' && (
        <SelectorDePortada
          ancla={botonPortada}
          actual={pagina.cover}
          alElegir={cambiarPortada}
          alCerrar={() => setSelector(null)}
        />
      )}
    </div>
  )
}
